import React, { useState, useEffect, useRef } from 'react';
import TextField from 'components/TextField';
import { 
  Container, 
  Row, 
  Column, 
  WrapperInputCustom, 
  AutocompleteWrapper, 
  AutocompleteDropdown, 
  AutocompleteItem 
} from './styles';

function CteMdfe({ formik, edit, view }) {
  const [municipiosCarregamento, setMunicipiosCarregamento] = useState([]);
  const [municipiosDescarregamento, setMunicipiosDescarregamento] = useState([]);
  const [showDropdownCarregamento, setShowDropdownCarregamento] = useState(false);
  const [showDropdownDescarregamento, setShowDropdownDescarregamento] = useState(false);
  const [municipioCarregamentoInput, setMunicipioCarregamentoInput] = useState('');
  const [municipioDescarregamentoInput, setMunicipioDescarregamentoInput] = useState('');
  const dropdownCarregamentoRef = useRef(null);
  const dropdownDescarregamentoRef = useRef(null);

  /**
   * Consultar municípios do IBGE por UF
   */
  const consultarMunicipiosIBGE = async (uf) => {
    if (!uf || uf.length !== 2) {
      return [];
    }

    try {
      const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf.toUpperCase()}/municipios`);
      if (!response.ok) {
        throw new Error('Erro ao consultar municípios');
      }
      const data = await response.json();
      return data.map(municipio => ({
        id: municipio.id,
        nome: municipio.nome,
        codigo_ibge: municipio.id
      }));
    } catch (error) {
      console.error('Erro ao consultar municípios do IBGE:', error);
      return [];
    }
  };

  /**
   * Buscar municípios quando UF de carregamento mudar
   */
  useEffect(() => {
    const buscarMunicipiosCarregamento = async () => {
      const uf = formik.values.uf_carregamento;
      if (uf && uf.length === 2) {
        const municipios = await consultarMunicipiosIBGE(uf);
        setMunicipiosCarregamento(municipios);
      } else {
        setMunicipiosCarregamento([]);
      }
    };

    buscarMunicipiosCarregamento();
  }, [formik.values.uf_carregamento]);

  /**
   * Buscar municípios quando UF de descarregamento mudar
   */
  useEffect(() => {
    const buscarMunicipiosDescarregamento = async () => {
      const uf = formik.values.uf_descarregamento;
      if (uf && uf.length === 2) {
        const municipios = await consultarMunicipiosIBGE(uf);
        setMunicipiosDescarregamento(municipios);
      } else {
        setMunicipiosDescarregamento([]);
      }
    };

    buscarMunicipiosDescarregamento();
  }, [formik.values.uf_descarregamento]);

  /**
   * Fechar dropdowns ao clicar fora
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownCarregamentoRef.current && !dropdownCarregamentoRef.current.contains(event.target)) {
        setShowDropdownCarregamento(false);
      }
      if (dropdownDescarregamentoRef.current && !dropdownDescarregamentoRef.current.contains(event.target)) {
        setShowDropdownDescarregamento(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Filtrar municípios de carregamento
   */
  const municipiosCarregamentoFiltrados = municipioCarregamentoInput
    ? municipiosCarregamento.filter(m => 
        m.nome.toLowerCase().includes(municipioCarregamentoInput.toLowerCase())
      )
    : municipiosCarregamento;

  /**
   * Filtrar municípios de descarregamento
   */
  const municipiosDescarregamentoFiltrados = municipioDescarregamentoInput
    ? municipiosDescarregamento.filter(m => 
        m.nome.toLowerCase().includes(municipioDescarregamentoInput.toLowerCase())
      )
    : municipiosDescarregamento;

  /**
   * Selecionar município de carregamento
   */
  const handleSelecionarMunicipioCarregamento = (municipio) => {
    formik.setFieldValue('codigo_municipio_carregamento', municipio.codigo_ibge);
    formik.setFieldValue('nome_municipio_carregamento', municipio.nome);
    setMunicipioCarregamentoInput(municipio.nome);
    setShowDropdownCarregamento(false);
  };

  /**
   * Selecionar município de descarregamento
   */
  const handleSelecionarMunicipioDescarregamento = (municipio) => {
    formik.setFieldValue('codigo_municipio_descarregamento', municipio.codigo_ibge);
    formik.setFieldValue('nome_municipio_descarregamento', municipio.nome);
    setMunicipioDescarregamentoInput(municipio.nome);
    setShowDropdownDescarregamento(false);
  };

  /**
   * Sincronizar input com valor do formik (apenas na inicialização)
   */
  useEffect(() => {
    if (formik.values.nome_municipio_carregamento && !municipioCarregamentoInput) {
      setMunicipioCarregamentoInput(formik.values.nome_municipio_carregamento);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas uma vez na montagem

  useEffect(() => {
    if (formik.values.nome_municipio_descarregamento && !municipioDescarregamentoInput) {
      setMunicipioDescarregamentoInput(formik.values.nome_municipio_descarregamento);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas uma vez na montagem
  return (
    <Container>
      <Row>
        <Column>
          <WrapperInputCustom>
            <TextField
              label="UF Carregamento"
              name="uf_carregamento"
              placeholder="Ex: SP"
              inputProps={{ maxLength: 2 }}
              value={formik.values.uf_carregamento || ''}
              onChange={(e) => {
                formik.handleChange(e);
                formik.setFieldValue('uf_carregamento', e.target.value.toUpperCase());
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.uf_carregamento && Boolean(formik.errors.uf_carregamento)}
              helperText={formik.touched.uf_carregamento && formik.errors.uf_carregamento}
              disabled={view}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <TextField
              label="Código Município Carregamento"
              name="codigo_municipio_carregamento"
              placeholder="Código IBGE"
              value={formik.values.codigo_municipio_carregamento || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.codigo_municipio_carregamento && Boolean(formik.errors.codigo_municipio_carregamento)}
              helperText={formik.touched.codigo_municipio_carregamento && formik.errors.codigo_municipio_carregamento || (formik.values.uf_carregamento ? 'Preenchido automaticamente ao selecionar o município' : 'Informe a UF primeiro')}
              disabled={view}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <AutocompleteWrapper ref={dropdownCarregamentoRef}>
              <TextField
                label="Nome Município Carregamento"
                name="nome_municipio_carregamento"
                placeholder="Digite para buscar o município"
                value={municipioCarregamentoInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setMunicipioCarregamentoInput(value);
                  setShowDropdownCarregamento(true);
                  // Se limpar o campo, limpar também o código
                  if (!value) {
                    formik.setFieldValue('codigo_municipio_carregamento', '');
                    formik.setFieldValue('nome_municipio_carregamento', '');
                  }
                }}
                onFocus={() => {
                  if (municipiosCarregamento.length > 0) {
                    setShowDropdownCarregamento(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowDropdownCarregamento(false);
                    formik.handleBlur({ target: { name: 'nome_municipio_carregamento' } });
                  }, 200);
                }}
                error={formik.touched.nome_municipio_carregamento && Boolean(formik.errors.nome_municipio_carregamento)}
                helperText={formik.touched.nome_municipio_carregamento && formik.errors.nome_municipio_carregamento || (
                  !formik.values.uf_carregamento || formik.values.uf_carregamento.length !== 2
                    ? 'Informe a UF primeiro'
                    : municipiosCarregamento.length > 0
                    ? `${municipiosCarregamento.length} município(s) encontrado(s)`
                    : 'Carregando municípios...'
                )}
                disabled={view || !formik.values.uf_carregamento || formik.values.uf_carregamento.length !== 2}
                fullWidth
                color="secondary"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              {showDropdownCarregamento && municipiosCarregamentoFiltrados.length > 0 && (
                <AutocompleteDropdown>
                  {municipiosCarregamentoFiltrados.slice(0, 20).map((municipio) => (
                    <AutocompleteItem
                      key={municipio.id}
                      onClick={() => handleSelecionarMunicipioCarregamento(municipio)}
                    >
                      {municipio.nome} (IBGE: {municipio.codigo_ibge})
                    </AutocompleteItem>
                  ))}
                  {municipiosCarregamentoFiltrados.length > 20 && (
                    <AutocompleteItem style={{ textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                      ... e mais {municipiosCarregamentoFiltrados.length - 20} município(s)
                    </AutocompleteItem>
                  )}
                </AutocompleteDropdown>
              )}
            </AutocompleteWrapper>
          </WrapperInputCustom>
        </Column>
      </Row>

      <Row>
        <Column>
          <WrapperInputCustom>
            <TextField
              label="UF Descarregamento"
              name="uf_descarregamento"
              placeholder="Ex: RJ"
              inputProps={{ maxLength: 2 }}
              value={formik.values.uf_descarregamento || ''}
              onChange={(e) => {
                formik.handleChange(e);
                formik.setFieldValue('uf_descarregamento', e.target.value.toUpperCase());
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.uf_descarregamento && Boolean(formik.errors.uf_descarregamento)}
              helperText={formik.touched.uf_descarregamento && formik.errors.uf_descarregamento}
              disabled={view}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <TextField
              label="Código Município Descarregamento"
              name="codigo_municipio_descarregamento"
              placeholder="Código IBGE"
              value={formik.values.codigo_municipio_descarregamento || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.codigo_municipio_descarregamento && Boolean(formik.errors.codigo_municipio_descarregamento)}
              helperText={formik.touched.codigo_municipio_descarregamento && formik.errors.codigo_municipio_descarregamento || (formik.values.uf_descarregamento ? 'Preenchido automaticamente ao selecionar o município' : 'Informe a UF primeiro')}
              disabled={view}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <AutocompleteWrapper ref={dropdownDescarregamentoRef}>
              <TextField
                label="Nome Município Descarregamento"
                name="nome_municipio_descarregamento"
                placeholder="Digite para buscar o município"
                value={municipioDescarregamentoInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setMunicipioDescarregamentoInput(value);
                  setShowDropdownDescarregamento(true);
                  // Se limpar o campo, limpar também o código
                  if (!value) {
                    formik.setFieldValue('codigo_municipio_descarregamento', '');
                    formik.setFieldValue('nome_municipio_descarregamento', '');
                  }
                }}
                onFocus={() => {
                  if (municipiosDescarregamento.length > 0) {
                    setShowDropdownDescarregamento(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowDropdownDescarregamento(false);
                    formik.handleBlur({ target: { name: 'nome_municipio_descarregamento' } });
                  }, 200);
                }}
                error={formik.touched.nome_municipio_descarregamento && Boolean(formik.errors.nome_municipio_descarregamento)}
                helperText={formik.touched.nome_municipio_descarregamento && formik.errors.nome_municipio_descarregamento || (
                  !formik.values.uf_descarregamento || formik.values.uf_descarregamento.length !== 2
                    ? 'Informe a UF primeiro'
                    : municipiosDescarregamento.length > 0
                    ? `${municipiosDescarregamento.length} município(s) encontrado(s)`
                    : 'Carregando municípios...'
                )}
                disabled={view || !formik.values.uf_descarregamento || formik.values.uf_descarregamento.length !== 2}
                fullWidth
                color="secondary"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              {showDropdownDescarregamento && municipiosDescarregamentoFiltrados.length > 0 && (
                <AutocompleteDropdown>
                  {municipiosDescarregamentoFiltrados.slice(0, 20).map((municipio) => (
                    <AutocompleteItem
                      key={municipio.id}
                      onClick={() => handleSelecionarMunicipioDescarregamento(municipio)}
                    >
                      {municipio.nome} (IBGE: {municipio.codigo_ibge})
                    </AutocompleteItem>
                  ))}
                  {municipiosDescarregamentoFiltrados.length > 20 && (
                    <AutocompleteItem style={{ textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                      ... e mais {municipiosDescarregamentoFiltrados.length - 20} município(s)
                    </AutocompleteItem>
                  )}
                </AutocompleteDropdown>
              )}
            </AutocompleteWrapper>
          </WrapperInputCustom>
        </Column>
      </Row>

      <Row>
        <Column>
          <WrapperInputCustom>
            <TextField
              label="Observação"
              name="note"
              placeholder="Observação"
              multiline
              rows={2}
              value={formik.values.note || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={view}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <TextField
              label="Informações para o Fisco"
              name="informacoes_fisco"
              placeholder="Informações adicionais para o fisco"
              multiline
              rows={2}
              value={formik.values.informacoes_fisco || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={view}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <TextField
              label="Informações Complementares"
              name="informacoes_complementares"
              placeholder="Informações complementares"
              multiline
              rows={2}
              value={formik.values.informacoes_complementares || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={view}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </WrapperInputCustom>
        </Column>
      </Row>
    </Container>
  );
}

export default CteMdfe;


